import { ValidationError, ValidationErrorType, ValidatorResult } from '@onrunning/backstage-models';
import axios, { HttpStatusCode, type AxiosRequestConfig, type AxiosResponse, type Method } from 'axios';
import { isString } from '../composables/is-string';
import { useUserStore } from '../stores/user';

export class CrudService<T = any> {
  private apiUrl!: string;

  constructor(controller?: string) {
    if (import.meta.env.VITE_APP_onBackstageAPI) {
      this.apiUrl = import.meta.env.VITE_APP_onBackstageAPI + (controller ? controller + '/' : '');
    } else {
      throw new Error(
        'you need to define a global environment variable and add a value to the onBackstageAPI key/property',
      );
    }
  }

  public async get(id: string, config?: AxiosRequestConfig): Promise<ValidatorResult<T>> {
    const result = new ValidatorResult<T>(undefined as any);
    try {
      const response = await axios.get(this.apiUrl + id, config);

      this.updateJwt(response);

      result.model = response.data;
    } catch (exception) {
      this.onError(exception, result);
    }

    return result;
  }

  public async getAll(
    /**
     * (optional; default: `automatic`) How to handle errors. `throw` will
     * result in errors being thrown as-is (i.e., an axios error object with a
     * `response` property -- see
     * https://github.com/axios/axios#handling-errors). `automatic` will handle
     * errors automatically, including showing a popup with an error message
     * and a redirect to the login page if needed. `silent` will do nothing
     * with the error.
     */
    errorHandling: 'throw' | 'automatic' | 'silent' = 'automatic',
    config?: AxiosRequestConfig,
  ): Promise<ValidatorResult<T[]>> {
    const result = new ValidatorResult<T[]>(undefined as any);
    try {
      const response = await axios.get(this.apiUrl, config);

      this.updateJwt(response);

      result.model = response.data;
    } catch (exception) {
      if (errorHandling === 'automatic') {
        this.onError(exception, result);
      }
      if (errorHandling === 'throw') {
        throw exception;
      }
    }

    return result;
  }

  public async post(model: T, config?: AxiosRequestConfig): Promise<ValidatorResult<T>> {
    const result = new ValidatorResult<T>(model);
    try {
      const response = await axios.post(this.apiUrl, model, config);

      this.updateJwt(response);

      result.model = response.data;
    } catch (exception) {
      this.onError(exception, result);
    }

    return result;
  }

  public async put(id: any, model: T, config?: AxiosRequestConfig): Promise<ValidatorResult<T>> {
    const result = new ValidatorResult<T>(model);
    try {
      const response = await axios.put(this.apiUrl + id, model, config);

      this.updateJwt(response);
    } catch (exception) {
      this.onError(exception, result);
    }

    return result;
  }

  public async remove(id: any, config?: AxiosRequestConfig): Promise<ValidatorResult<T>> {
    const result = new ValidatorResult<T>(undefined as any);
    try {
      const response = await axios.delete(this.apiUrl + id, config);

      this.updateJwt(response);
    } catch (exception) {
      this.onError(exception, result);
    }

    return result;
  }

  public async do(
    method: string,
    endpoint: string,
    body?: any,
    headers?: any,

    /**
     * (optional; default: `automatic`) How to handle errors. `throw` will
     * result in errors being thrown as-is (i.e., an axios error object with a
     * `response` property -- see
     * https://github.com/axios/axios#handling-errors). `automatic` will handle
     * errors automatically, including showing a popup with an error message
     * and a redirect to the login page if needed. `silent` will do nothing
     * with the error.
     */
    errorHandling: 'throw' | 'automatic' | 'silent' = 'automatic',
  ): Promise<ValidatorResult<any>> {
    const result = new ValidatorResult<any>(undefined);
    try {
      const requestOptions: AxiosRequestConfig = {
        url: this.apiUrl + endpoint,
        method: method as Method,
      };
      if (body) {
        requestOptions.data = body;
      }
      if (headers) {
        requestOptions.headers = headers;
      }

      const response = await axios.request(requestOptions);

      this.updateJwt(response);

      result.model = response.data;
    } catch (exception) {
      if (errorHandling === 'automatic') {
        this.onError(exception, result);
      }
      if (errorHandling === 'throw') {
        throw exception;
      }
    }

    return result;
  }

  public async doWithCustomType<U>(
    method: string,
    endpoint: string,
    body?: any,
    headers?: any,

    /**
     * (optional; default: `automatic`) How to handle errors. `throw` will
     * result in errors being thrown as-is (i.e., an axios error object with a
     * `response` property -- see
     * https://github.com/axios/axios#handling-errors). `automatic` will handle
     * errors automatically, including showing a popup with an error message
     * and a redirect to the login page if needed. `silent` will do nothing
     * with the error.
     */
    errorHandling: 'throw' | 'automatic' | 'silent' = 'automatic',
  ): Promise<ValidatorResult<U>> {
    const result = new ValidatorResult<U>(undefined as any);
    try {
      const requestOptions: AxiosRequestConfig = {
        url: this.apiUrl + endpoint,
        method: method as Method,
      };

      if (body) {
        requestOptions.data = body;
      }

      if (headers) {
        requestOptions.headers = headers;
      }

      const response = await axios.request(requestOptions);

      this.updateJwt(response);

      //todo ww don't add errors into the results, this means results.isValid() will always be true even there is no model.
      result.model = response.data;
    } catch (exception) {
      if (errorHandling === 'automatic') {
        this.onError(exception, result);
      }

      if (errorHandling === 'throw') {
        throw exception;
      }
    }

    return result;
  }

  public async downloadFile(
    endpoint: string,
    payload: any,
    fileName?: string,
    config?: AxiosRequestConfig,
  ): Promise<void> {
    try {
      const method = config?.method?.toLowerCase() || 'post';
      const requestConfig: AxiosRequestConfig = {
        ...config,
        url: this.apiUrl + endpoint,
        method: method as Method,
        responseType: 'blob',
      };

      if (payload && method !== 'get') {
        requestConfig.data = payload;
      }

      const response = await axios.request(requestConfig);

      if (response.status !== HttpStatusCode.Ok) {
        throw new Error('File download failed');
      }

      this.updateJwt(response);

      const filename =
        fileName || this.extractFilename(response.headers['content-disposition']) || 'backstage-order-download.xlsx';
      CrudService.triggerDownload(response.data, filename);
    } catch (error) {
      console.error('Error downloading file:', error);
      throw error;
    }
  }
  public static triggerDownload(data: BlobPart | Blob, filename: string): void {
    const blob = data instanceof Blob ? data : new Blob([data]);
    const url = window.URL.createObjectURL(blob);

    const link = document.createElement('a');
    link.href = url;
    link.download = filename;

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    window.URL.revokeObjectURL(url);
  }

  private extractFilename(contentDisposition?: string): string | null {
    if (!contentDisposition) return null;
    const match = contentDisposition.match(/filename="([^"]+)"/);

    return match?.[1] || null;
  }

  private updateJwt(response?: AxiosResponse) {
    if (response?.headers['x-jwt-cookie']) {
      localStorage.setItem('jwt', response.headers['x-jwt-cookie']);
    }
  }

  private onError(exception: any, result: ValidatorResult<any>) {
    // let globalErrors: ValidationError[] = [];

    if (exception?.response?.status) {
      result.statusCode = exception.response.status;
    }

    // remove this, since it didn't create any value for us, revisit later
    // if (exception?.response && exception?.response?.data) {
    //   result.errors = this.normalizeErrors(exception.response.data);
    //   if (result.errors.length > 0) {
    //     window?.newrelic?.noticeError('api call error', { api_errors: result.errors.map((e) => e.error) });
    //   }
    // }

    // if (result.errors.length) {
    //   globalErrors = result.errors.filter((error: ValidationError) => error.errorType === 'global');
    // }

    if (exception?.response?.status === 401) {
      const { logout } = useUserStore();
      logout();

      return;
    }

    // ToDo: change to a composable?
    // const errorMsg = globalErrors?.length ? i18n.t(globalErrors[0].error) : i18n.t('global.error');

    // this.$snotify.error(errorMsg);
  }

  /**
   * Normalize different error response formats into a consistent array format
   */
  private normalizeErrors(data: any): Array<ValidationError> {
    // array format: [{"errorType":"global","error":"Contact ID is required", injectValues: { key: value } }]
    if (Array.isArray(data) && data.length > 0 && data[0].errorType && data[0].error) {
      return data;
    }

    // handle string message error, for example: { "message": "Contact ID is required" }
    // this will be ignored since its not translated
    if (isString(data?.message)) {
      return [{ error: '', errorType: ValidationErrorType.global }];
    }

    /** 
     * DTO errors 
      {
          "message": [
              "contactId should not be empty"
          ],
          "error": "Bad Request",
          "statusCode": 400
      }
      this will be ignored since its not translated
     * */
    if (data?.message && Array.isArray(data.message)) {
      return [{ error: '', errorType: ValidationErrorType.global }];
    }

    return [];
  }
}
